/**
 * StudioStartMenu — the Check Builder's start/design choices as
 * menu items in the app shell (hamburger drawer on mobile, sidebar
 * on desktop).
 *
 * These choices used to render as a stacked bar over the studio's
 * preview pane; Princewill (2026-11-04): they belong in the
 * hamburger menu — "When I click it I select. Not it blocking
 * everywhere." The studio now presents its canvas immediately and
 * this section carries the choices instead.
 *
 * The component is presentation only: it reads whether a previous
 * check is on offer from the studio start bridge and dispatches
 * the chosen action back through it. CheckBuilder owns the actual
 * handlers (accept/dismiss the previous check, open My Designs,
 * open the patterns section) — one canonical implementation, two
 * former entry points, now just this one.
 */

import React, { useEffect, useState } from 'react';
import { FiRotateCcw, FiFile, FiFolder, FiGrid } from 'react-icons/fi';
import {
    currentStudioStartOffer,
    subscribeStudioStartOffer,
    requestStudioStartAction,
    type StudioStartAction
} from '../utils/studioStartBridge';

interface StudioStartMenuProps {
    collapsed?: boolean;
    onNavigate?: () => void;
}

const StudioStartMenu: React.FC<StudioStartMenuProps> = ({ collapsed = false, onNavigate }) => {
    const [hasPrevious, setHasPrevious] = useState<boolean>(() => currentStudioStartOffer());

    useEffect(() => subscribeStudioStartOffer(setHasPrevious), []);

    const run = (action: StudioStartAction) => {
        requestStudioStartAction(action);
        if (onNavigate) onNavigate();
    };

    const item = (action: StudioStartAction, icon: React.ReactNode, label: string, primary = false) => (
        <button
            key={action}
            type="button"
            data-start-menu-item={action}
            onClick={() => run(action)}
            className={`flex items-center gap-2.5 px-2.5 py-2.5 rounded-2xl text-[13px] font-medium transition-all duration-200 group w-full text-left
                ${primary
                    ? 'text-primary-300 bg-primary-500/10 hover:bg-primary-500/20 ring-1 ring-primary-500/25'
                    : 'text-slate-400 hover:text-white hover:bg-white/[0.06]'}
                ${collapsed ? 'justify-center' : ''}
            `}
            title={collapsed ? label : undefined}
        >
            <span className={`flex-shrink-0 ${primary ? 'text-primary-300' : 'text-slate-500 group-hover:text-white'} transition-colors`}>
                {icon}
            </span>
            {!collapsed && <span className="flex-1 truncate">{label}</span>}
        </button>
    );

    return (
        <div data-start-menu className="space-y-1">
            {!collapsed && (
                <div className="px-2.5 pt-1 pb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                    Start
                </div>
            )}
            {hasPrevious && item('previous', <FiRotateCcw size={18} />, 'Start with my previous check', true)}
            {hasPrevious && item('blank', <FiFile size={18} />, 'Start blank')}
            {item('designs', <FiFolder size={18} />, 'My saved designs')}
            {item('patterns', <FiGrid size={18} />, 'Browse patterns')}
        </div>
    );
};

export default StudioStartMenu;
